import * as Styled from './EventImgContent.styles';

import { defaultValues, isNewImg, isUploadedOrFetched } from './utils';
import { getContent, updateContent } from './services';
import { useMutation, useQuery } from '@tanstack/react-query';

import { FieldProps } from './types';
import { FlexContainer } from 'features/common/components/FlexContainer';
import { Header } from 'features/common/components/Header';
import { IdParamsType } from 'utils/types';
import { MainLayout } from 'layouts/MainLayout';
import { MediaContent } from './MediaContent';
import { PunchupPageShell } from 'features/common/components/Punchup/pages/PunchupPageShell';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useUploadImageMutation } from './useUploadImageMutation';
import { useAppSelector } from 'store/hooks';
import { PunchupButton } from 'features/common/components/Punchup/primitives/buttons/PunchupButton';

export const EventImgContent = () => {
	const { t } = useTranslation();
	const { id }: IdParamsType = useParams();
	const eventId = parseInt(id);
	const { isPunchup } = useAppSelector((state) => state.auth);

	const { data: initialValues } = useQuery({
		queryKey: ['event-content', id],
		queryFn: () => getContent(id),
	});

	const formMethods = useForm({
		defaultValues,
		values: initialValues,
	});

	const { mutate: mutateWebsite } = useMutation(updateContent);

	const { mutateUploadImage } = useUploadImageMutation(formMethods);

	const handleSubmit = async (attributes: FieldProps) => {
		const [image] = attributes.mediaContent.eventImage;
		if (image && initialValues && isNewImg(image)) {
			await mutateUploadImage({
				image,
				prefix: image.name,
				index: 1,
				type: 'event_image',
			});
		}

		const allMedia = attributes.mediaContent.media;
		if (allMedia) {
			await Promise.all(
				allMedia.map(async (image, index) => {
					if (isNewImg(image)) {
						await mutateUploadImage({
							image,
							prefix: image.name,
							index: index,
							type: 'gallery_image',
						});
					}
				})
			);
		}

		const values = formMethods.getValues();
		values.mediaContent.media.forEach((media, index) => {
			if (isUploadedOrFetched(media)) media.position = index;
		});

		mutateWebsite({ attributes: values, eventId });
	};

	const content = (
		<Styled.ContentWrapper>
			<Styled.Container onSubmit={handleSubmit} formMethods={formMethods} isPunchup={isPunchup}>
				<MediaContent />
				<Styled.DividerWithSpacing />
				<FlexContainer gap="12px" justifyContent="center">
					{isPunchup ? (
						<PunchupButton text={t('button-save')} type="submit" data-cy="save-btn" />
					) : (
						<Styled.PublishButton text={t('button-save')} type="submit" data-cy="save-btn" />
					)}
				</FlexContainer>
			</Styled.Container>
		</Styled.ContentWrapper>
	);

	if (isPunchup) {
		return <PunchupPageShell pageName="Media Content">{content}</PunchupPageShell>;
	}

	return (
		<MainLayout>
			<Header.TitledHeader title={t('eventImgContent-header')} subtitle={initialValues?.eventName || ''} />
			{content}
		</MainLayout>
	);
};
